
import moment from "moment";
import DatePickerComponent from "../../../../components/date-picker/date-picker.component";
import paginationComponent from "../../../../components/pagination/pagination.component";
import { formats } from "../../../../config/constants";
import ViewBase from "../../../view-base.view";
import IMEIExtendedWarrantyUploadDialog from "./imei-extended-warranty.excel-upload";
import IMEIExtendedWarrantyModel from "../../../../models/imei-extended-warranty/imei-extended-warranty.model";

/**
 * IMEI Extended Warranty View
 * 
 * A page that displays a list of IMEI Extended Warranty
 * 
 * @author cmcnkhiep
 */

export default function IMEIExtendedWarranty() {

  ViewBase.call(this);

  const DESCENDING_TEXT = 'DESC';
  const ASCENDING_TEXT = 'ASC';
  const ALIAS_VALUES = { Y: 'Yes', N: 'No' }

  const DEFAULT_FILTERS = {
    modelCode: '',
    createFrom: '',
    createTo: '',
    updateFrom: '',
    updateTo: ''
  };

  const DEFAULT_PAGINATION = {
    pageSize: 10,
    pageIndex: 1,
    totalItems: 0
  };

  let filters = structuredClone(DEFAULT_FILTERS);
  let currentSearchFilters = structuredClone(DEFAULT_FILTERS);
  let pagination = structuredClone(DEFAULT_PAGINATION);
  let selectedSortBy = '';
  let selectedOrderBy = '';
  let dataSource = [];
  let isExcelUploadOpen = false;

  const flipOrderText = (orderText) => {
    return orderText.toUpperCase() === DESCENDING_TEXT ? ASCENDING_TEXT : DESCENDING_TEXT;
  }

  const onSortClick = (orderBy) => {
    if (!dataSource.length) {
      return
    }
    if (selectedOrderBy === orderBy) {
      selectedSortBy = flipOrderText(selectedSortBy);
    } else {
      selectedOrderBy = orderBy;
      selectedSortBy = DESCENDING_TEXT;
    }
    searchIMEIExtendedWarranty();
  };

  const getSortIcon = (orderBy) => {
    if (selectedOrderBy !== orderBy) {
      return null;
    }

    return selectedSortBy === DESCENDING_TEXT
      ? m('i.bi.bi-sort-down', { "style": "float:left" })
      : m('i.bi.bi-sort-up', { "style": "float:left" });
  };

  const toQueryParams = (params) => {
    return Object.assign(
      {
        pageSize: pagination.pageSize,
        pageIndex: pagination.pageIndex,
        sortBy: selectedSortBy,
        orderBy: selectedOrderBy,
        modelCode: params.modelCode,
        createFrom: params.createFrom.trim() ? moment(params.createFrom).format(formats.apiDateFormat) : '',
        createTo: params.createTo.trim() ? moment(params.createTo).format(formats.apiDateFormat) : '',
        updateFrom: params.updateFrom.trim() ? moment(params.updateFrom).format(formats.apiDateFormat) : '',
        updateTo: params.updateTo.trim() ? moment(params.updateTo).format(formats.apiDateFormat) : ''
      },
    );
  };

  const clearFilter = () => {
    filters = structuredClone(DEFAULT_FILTERS);
  };

  const errorMessageMap = {
    create: this.getText('EM00000804', 'Create Date is invalid!'),
    update: this.getText('EM00000805', 'Update Date is invalid!'),
  }
  const checkInvalidField = (payload) => {
    let set = new Set();
    Object.entries(payload).forEach(([key, value]) => {
      if (value == 'Invalid date') {
        set.add(key.substring(0, 6))
      }
    })
    return [...set.values()]
  }

  const searchIMEIExtendedWarranty = () => {
    this.showLoading();
    IMEIExtendedWarrantyModel.searchIMEIExtendedWarranty(toQueryParams(currentSearchFilters))
      .then((result) => {
        dataSource = result.data
        pagination.pageIndex = result.pageIndex;
        pagination.pageSize = result.pageSize;
        pagination.totalItems = result.total;
      })
      .catch((error) => {
        this.handleError(error);
      }).finally(() => {
        this.hideLoading();
      })
  }

  const handleSubmitSearch = (e) => {
    e.preventDefault();
    const invalidFields = checkInvalidField(filters)
    if (invalidFields.length) {
      if (invalidFields.includes('create')) {
        filters.createFrom = ''
        filters.createTo = ''
      }
      if (invalidFields.includes('update')) {
        filters.updateFrom = ''
        filters.updateTo = ''
      }
      this.showMessage(invalidFields.map(key => errorMessageMap[key]))
      return
    };
    pagination = structuredClone(DEFAULT_PAGINATION);
    currentSearchFilters = structuredClone(filters);
    searchIMEIExtendedWarranty();
  }

  return {
    view: () => {
      return [
        /* SEARCH */
        m('.card.mb-3',
          m('.card-body',
            m('form', {
              onsubmit: handleSubmitSearch
            },
              m('.row.g-2',

                m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                  m('label.form-label[for="modelCodeAliasInput"]', this.getText("LT00000717", "Model")),
                  m('input#modelCodeAliasInput.form-control.form-control-bg[type=text]',
                    {
                      placeholder: this.getText("LT00000717", "Model"),
                      value: filters.modelCode,
                      onchange: (e) => { filters.modelCode = e.target.value; }
                    }
                  )
                ),
                m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                  m('label.form-label[for="createDateInput"]', this.getText("LT00000041", "Create Date")),
                  m(DatePickerComponent, {
                    id: 'createDateInput',
                    class: 'form-control',
                    placeholder: this.getText('LT00000070', 'Date Range'),
                    isRange: true,
                    dateStart: filters.createFrom,
                    dateEnd: filters.createTo,
                    onselect: (e) => {
                      filters.createFrom = moment(e.detail.start).format(formats.dateFormat);
                      filters.createTo = moment(e.detail.end).format(formats.dateFormat);
                    }
                  })
                ),
                m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                  m('label.form-label[for="updateDateInput"]', this.getText("LT00000306", "Update Date")),
                  m(DatePickerComponent, {
                    id: 'updateDateInput',
                    class: 'form-control',
                    placeholder: this.getText('LT00000070', 'Date Range'),
                    isRange: true,
                    dateStart: filters.updateFrom,
                    dateEnd: filters.updateTo,
                    onselect: (e) => {
                      filters.updateFrom = moment(e.detail.start).format(formats.dateFormat);
                      filters.updateTo = moment(e.detail.end).format(formats.dateFormat);
                    }
                  })
                ),
                m('.col-12.d-flex.justify-content-end.gap-2',
                  m('button.btn.btn-primary',
                    {
                      type: 'submit',
                    },
                    this.getText("LT00000001", "Search")
                  ),
                  m('button.btn.btn-white',
                    {
                      type: 'button',
                      onclick: () => {
                        clearFilter();
                      }
                    },
                    this.getText("LT00000031", "Clear")
                  )
                )
              )
            ),

          )
        ),

        /* TABLE */
        m('.card',
          m('.card-body',
            m('.row.g-2',
              m('.col-12.d-flex.justify-content-end',
                m('button.btn.btn-primary',
                  {
                    onclick: () => { isExcelUploadOpen = true; }
                  },
                  m('i.bi.bi-file-earmark-arrow-up-fill'),
                  this.getText('LT00000197', 'Excel Upload')
                ),
              ),
              m('.col-12',
                m('.table-responsive',
                  m('table.table.table-hover.sticky-column-start',
                    m('thead',
                      m('tr.align-middle',
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('modelCode'); } },
                          getSortIcon('modelCode'),
                          this.getText('LT00000717', 'Model')
                        ),
                        m('th.cursor-pointer.text-capitalize[scope="col"]',
                          { onclick: () => { onSortClick('alias'); } },
                          getSortIcon('alias'),
                          this.getText('LT00000956', 'Alias').toLowerCase()
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('extendedWarrantyPeriod'); } },
                          getSortIcon('extendedWarrantyPeriod'),
                          this.getText('LT00008042', 'Warranty(Months)')
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('validFrom'); } },
                          getSortIcon('validFrom'),
                          this.getText('LT00008043', 'Valid From')
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('validTo'); } },
                          getSortIcon('validTo'),
                          this.getText('LT00008044', 'Valid To')
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('createBy'); } },
                          getSortIcon('createBy'),
                          this.getText('LT00000709', 'Created By')
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('createDate'); } },
                          getSortIcon('createDate'),
                          this.getText('LT00000885', 'Created On')
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('updateBy'); } },
                          getSortIcon('updateBy'),
                          this.getText('LT00000710', 'Updated By')
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('updateDate'); } },
                          getSortIcon('updateDate'),
                          this.getText('LT00000887', 'Updated On')
                        ),
                      )
                    ),
                    m('tbody',
                      (dataSource.length == 0) &&
                      m('tr.align-middle',
                        m('td[colSpan=9]', this.getText("EM00000141", "No records found."))
                      ),
                      dataSource.length > 0 &&
                      dataSource.map((data) =>
                        m('tr.align-middle',
                          m('td', m('span.cursor-text', data.modelCode)),
                          m('td', m('span.cursor-text', ALIAS_VALUES[data.alias])),
                          m('td', m('span.cursor-text', data.extendedWarrantyPeriod)),
                          m('td', m('span.cursor-text', data.validFrom.trim() ? moment(data.validFrom).format(formats.dateFormat) : '')),
                          m('td', m('span.cursor-text', data.validTo.trim() ? moment(data.validTo).format(formats.dateFormat) : '')),
                          m('td', m('span.cursor-text', data.createBy)),
                          m('td', m('span.cursor-text', moment(data.createDate).format(formats.LOCAL_TIME))),
                          m('td', m('span.cursor-text', data.updateBy)),
                          m('td', m('span.cursor-text', moment(data.updateDate).format(formats.LOCAL_TIME)))
                        )
                      )
                    )
                  )
                )
              ),
              dataSource.length > 0 &&
              m('.col-12', m(paginationComponent,
                {
                  currentPage: pagination.pageIndex,
                  pageSize: pagination.pageSize,
                  totalItems: pagination.totalItems,
                  onPageChanged: (pageIndex) => {
                    pagination.pageIndex = pageIndex;
                    searchIMEIExtendedWarranty();
                  },
                  onPageSizeChanged: (pageIndex, pageSize) => {
                    pagination.pageSize = pageSize;
                    pagination.pageIndex = pageIndex;
                    searchIMEIExtendedWarranty();
                  }
                }
              )),
            )
          )
        ),

        /* EXCEL UPLOAD DIALOG */
        m(IMEIExtendedWarrantyUploadDialog,
          {
            filters,
            isOpen: isExcelUploadOpen,
            onClose: (isReloadTable) => {
              isExcelUploadOpen = false;
              if (isReloadTable === true) {
                searchIMEIExtendedWarranty();
              }
            }
          }
        ),
        this.getBaseViews()
      ];
    }
  };
}
